import { useState } from 'react';
import { Form, Input, Modal, Select, notification } from 'antd';
import CustomButton from 'components/custom-button';
import type { StudioTeamRow } from '../contract';
import { CreateTeamBody, EditTeamBody, createStudioTeam, editStudioTeam } from '../api';
import { mapStudioError } from '../studio-error';

/** Thành viên Studio chọn được khi tạo nhóm (từ StudioMemberRow). */
export interface TeamFormMemberOption {
  key: string;
  name: string;
  /** Nhóm trưởng phải có tài khoản app (STUDIO_TEAM_LEADER_INVALID). */
  userId: number | null;
  /** Đang ở Nhóm nhảy khác: không chọn được khi tạo (S15 không tự chuyển). */
  team?: { id: number; name: string } | null;
}

export interface TeamFormModalProps {
  open: boolean;
  studioId: number;
  /** Có: sửa nhóm (S16). Không: tạo nhóm (S15). */
  team?: StudioTeamRow | null;
  /** Tạo nhóm: danh sách chọn thành viên ban đầu (≤ 50) và Nhóm trưởng. */
  memberOptions?: TeamFormMemberOption[];
  onClose: () => void;
  onDone?: (team: StudioTeamRow) => void;
}

interface FormValues {
  name: string;
  memberKeys?: string[];
  leaderKey?: string | null;
  note?: string;
}

const MAX_MEMBERS = 50;

/**
 * Tạo / sửa Nhóm nhảy (S15, S16; mục 8.7.6). Form KHÔNG có ô UID kênh (UID kênh chỉ Admin đặt bằng
 * TeamChannelModal). Sửa chỉ gửi trường đã đổi; `leaderKey: null` = bỏ Nhóm trưởng.
 */
const TeamFormModal = ({
  open,
  studioId,
  team,
  memberOptions = [],
  onClose,
  onDone,
}: TeamFormModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const editing = !!team;
  const pickedKeys = Form.useWatch('memberKeys', form) || [];
  const currentLeaderKey = team?.members.find((m) => m.role === 'LEADER')?.key ?? null;

  const leaderOptions = editing
    ? (team?.members || [])
        .filter((m) => m.userId !== null)
        .map((m) => ({ value: m.key, label: m.name }))
    : memberOptions
        .filter((m) => pickedKeys.includes(m.key) && m.userId !== null)
        .map((m) => ({ value: m.key, label: m.name }));

  const handleClose = () => {
    if (saving) return;
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: FormValues) => {
    const name = values.name.trim();
    const note = values.note?.trim() || '';
    setSaving(true);
    try {
      let row: StudioTeamRow;
      if (team) {
        const body: EditTeamBody = {};
        if (name !== team.name) body.name = name;
        const leaderKey = values.leaderKey ?? null;
        if (leaderKey !== currentLeaderKey) body.leaderKey = leaderKey;
        if (note !== (team.note ?? '')) body.note = note;
        if (!Object.keys(body).length) {
          notification.info({ message: 'Chưa có thay đổi.' });
          return;
        }
        row = await editStudioTeam(studioId, team.id, body);
        notification.success({ message: `Đã sửa ${row?.name || name}.` });
      } else {
        const body: CreateTeamBody = { name };
        if (values.memberKeys?.length) body.memberKeys = values.memberKeys;
        if (values.leaderKey && values.memberKeys?.includes(values.leaderKey)) {
          body.leaderKey = values.leaderKey;
        }
        if (note) body.note = note;
        row = await createStudioTeam(studioId, body);
        notification.success({ message: `Đã tạo ${row?.name || name}.` });
      }
      form.resetFields();
      onDone?.(row);
      onClose();
    } catch (err) {
      notification.error({ message: mapStudioError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={editing ? `Sửa ${team?.name}` : 'Tạo Nhóm nhảy'}
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={560}
    >
      {/* destroyOnClose: Form dựng lại mỗi lần mở nên initialValues luôn theo nhóm đang sửa. */}
      <Form
        form={form}
        layout="vertical"
        className="custom-form"
        initialValues={{
          name: team?.name ?? '',
          leaderKey: currentLeaderKey ?? undefined,
          note: team?.note ?? '',
        }}
        onFinish={onFinish}
      >
        <Form.Item
          label="Tên Nhóm nhảy"
          name="name"
          rules={[
            { required: true, whitespace: true, message: 'Nhập tên Nhóm nhảy.' },
            { max: 120, message: 'Tên tối đa 120 ký tự.' },
          ]}
        >
          <Input maxLength={120} placeholder="VD: Nhóm A" />
        </Form.Item>
        {!editing && (
          <Form.Item
            label="Thành viên ban đầu (không bắt buộc)"
            name="memberKeys"
            extra={`Tối đa ${MAX_MEMBERS} người. Người đang ở Nhóm nhảy khác cần chuyển nhóm sau khi tạo.`}
          >
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              placeholder="Chọn thành viên của Studio"
              maxCount={MAX_MEMBERS}
              options={memberOptions.map((m) => ({
                value: m.key,
                label: m.team ? `${m.name} (đang ở ${m.team.name})` : m.name,
                disabled: !!m.team,
              }))}
            />
          </Form.Item>
        )}
        <Form.Item
          label="Nhóm trưởng (không bắt buộc)"
          name="leaderKey"
          extra="Nhóm trưởng phải là thành viên có tài khoản app của nhóm."
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={editing ? 'Chưa có Nhóm trưởng' : 'Chọn trong thành viên ban đầu'}
            options={leaderOptions}
          />
        </Form.Item>
        <Form.Item label="Ghi chú (không bắt buộc)" name="note">
          <Input.TextArea rows={2} maxLength={255} showCount />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text={editing ? 'Lưu' : 'Tạo Nhóm nhảy'}
            bgColor="secondary-green"
            htmlType="submit"
            loading={saving}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default TeamFormModal;
